import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, ChevronUp, Clock, Search } from 'lucide-react';
import { TeacherProfilesSearch } from './TeacherProfilesSearch';
import { TeacherProfilesFilters } from './TeacherProfilesFilters';
import { TeacherStatusToggle } from './TeacherStatusToggle';
import { FilterState } from '@/hooks/useTeacherProfilesFilters';

interface TeacherProfilesControlPanelProps {
  includeInactive: boolean;
  onToggleChange: (includeInactive: boolean) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filters: FilterState;
  filterOptions: {
    subjects: string[];
    gradeLevels: string[];
    courseTypes: string[];
    specialized: string[];
  };
  onFilterChange: (filterType: 'subject' | 'gradeLevel' | 'courseType' | 'specialized', value: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  onAvailabilityFilter?: (day: string, startTime: string, endTime: string) => void;
  onClearAvailabilityFilter?: () => void;
  currentAvailabilityFilter?: { day: string; startTime: string; endTime: string } | null;
}

export const TeacherProfilesControlPanel: React.FC<TeacherProfilesControlPanelProps> = ({
  includeInactive,
  onToggleChange,
  searchQuery,
  onSearchChange,
  filters,
  filterOptions,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
  onAvailabilityFilter,
  onClearAvailabilityFilter,
  currentAvailabilityFilter,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');

  // สร้างตัวเลือกเวลา 6:00 - 22:00 ทุก 30 นาที
  const timeOptions = [];
  for (let hour = 6; hour <= 22; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  const dayOptions = [
    { value: '1', label: 'จันทร์' },
    { value: '2', label: 'อังคาร' },
    { value: '3', label: 'พุธ' },
    { value: '4', label: 'พฤหัสบดี' },
    { value: '5', label: 'ศุกร์' },
    { value: '6', label: 'เสาร์' },
    { value: '7', label: 'อาทิตย์' },
  ];

  const getDayLabel = (dayValue: string) => {
    return dayOptions.find(day => day.value === dayValue)?.label || dayValue;
  };

  const handleSearchAvailableTeachers = () => {
    if (selectedDay && startTime && endTime && onAvailabilityFilter) {
      onAvailabilityFilter(selectedDay, startTime, endTime);
    }
  };

  const handleClearAvailabilityFilter = () => {
    setSelectedDay('');
    setStartTime('');
    setEndTime('');
    if (onClearAvailabilityFilter) {
      onClearAvailabilityFilter();
    }
  };

  const isSearchDisabled = !selectedDay || !startTime || !endTime;

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">ตัวควบคุมและค้นหา</CardTitle>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="p-1">
                {isOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
          </div>
        </CardHeader>
        
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Status Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">ตัวเลือกการแสดงผล</span>
              <TeacherStatusToggle
                includeInactive={includeInactive}
                onToggle={onToggleChange}
              />
            </div>

            {/* Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">ค้นหาครู</label>
              <TeacherProfilesSearch
                searchQuery={searchQuery}
                onSearchChange={onSearchChange}
              />
            </div>

            {/* Filters */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">ตัวกรองข้อมูล</label>
              <TeacherProfilesFilters
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={onFilterChange}
                onClearFilters={onClearFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </div>

            {/* Availability Filter */}
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <label className="text-sm font-medium text-blue-800">ค้นหาครูว่างตามตารางเวลา</label>
                </div>
                {currentAvailabilityFilter && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded">
                      วันที่ {getDayLabel(currentAvailabilityFilter.day)} {currentAvailabilityFilter.startTime} - {currentAvailabilityFilter.endTime}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearAvailabilityFilter}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      ล้าง
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* วัน */}
                <div className="space-y-1">
                  <label className="text-xs text-blue-700">วัน</label>
                  <Select value={selectedDay} onValueChange={setSelectedDay}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="เลือกวัน" />
                    </SelectTrigger>
                    <SelectContent>
                      {dayOptions.map((day) => (
                        <SelectItem key={day.value} value={day.value}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* เวลาเริ่มต้น */}
                <div className="space-y-1">
                  <label className="text-xs text-blue-700">เวลาเริ่มต้น</label>
                  <Select value={startTime} onValueChange={setStartTime}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="เลือกเวลา" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* เวลาสิ้นสุด */}
                <div className="space-y-1">
                  <label className="text-xs text-blue-700">เวลาสิ้นสุด</label>
                  <Select value={endTime} onValueChange={setEndTime}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="เลือกเวลา" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end gap-2">
                  <Button
                    onClick={handleSearchAvailableTeachers}
                    disabled={isSearchDisabled}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                  >
                    <Search className="h-4 w-4" />
                      ค้นหาครูว่าง
                    </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
